import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "../i18n";
import { Helmet } from "react-helmet-async";

const API_BASE   = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";
const OTP_LENGTH = 6;
const OTP_EXPIRY = 120;

function pad2(n) { return String(n).padStart(2, "0"); }
function fmtTime(s) { return `${pad2(Math.floor(s / 60))}:${pad2(s % 60)}`; }

export default function RegisterPage() {
  const { t, lang } = useTranslation();
  const isRTL = lang === "ar";
  const navigate = useNavigate();

  // ── Step: "form" | "otp" ─────────────────────────────────────────────────
  const [step, setStep] = useState("form");

  // ── Form state ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    p1: "",
    p2: ""
  });
  const [status,        setStatus]        = useState("idle");
  const [errorMsg,      setErrorMsg]      = useState("");
  const [fieldErrors,   setFieldErrors]   = useState({ username: "", email: "", p1: "", p2: "" });
  const [showPasswords, setShowPasswords] = useState({ p1: false, p2: false });

  // ── OTP state ─────────────────────────────────────────────────────────────
  const [digits,    setDigits]    = useState(Array(OTP_LENGTH).fill(""));
  const [otpStatus, setOtpStatus] = useState("idle");
  const [otpError,  setOtpError]  = useState("");
  const [countdown, setCountdown] = useState(OTP_EXPIRY);
  const [resendSt,  setResendSt]  = useState("idle");
  const boxRefs  = useRef([]);
  const timerRef = useRef(null);

  // ── Password strength ─────────────────────────────────────────────────────
  const strength = useMemo(() => {
    let s = 0;
    const p = formData.p1;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  }, [formData.p1]);

  const getStrengthColor = (level) => {
    if (level <= 1) return "#ef4444";
    if (level <= 2) return "#f59e0b";
    if (level <= 3) return "#3b82f6";
    return "#10b981";
  };

  const getStrengthLabel = () => {
    if (strength <= 1) return t("strength_weak")   || "Weak";
    if (strength <= 2) return t("strength_fair")   || "Fair";
    if (strength <= 3) return t("strength_good")   || "Good";
    return               t("strength_strong") || "Strong";
  };

  // ── Validation ────────────────────────────────────────────────────────────
  const validateUsername = (value) => {
    if (!value.trim())                    return t("username_required")  || "Username is required.";
    if (value.length < 3)                 return t("username_too_short") || "Username must be at least 3 characters";
    if (!/^[a-zA-Z0-9_-]+$/.test(value)) return t("username_invalid")   || "Username can only contain letters, numbers, underscores, and hyphens";
    return "";
  };

  const validateEmail = (value) => {
    if (!value.trim())                              return t("email_required") || "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return t("email_invalid")  || "Please enter a valid email address";
    return "";
  };

  const validatePassword = (value) => {
    if (!value)               return t("password_required")     || "Password is required";
    if (value.length < 8)     return t("password_too_short")    || "Password must be at least 8 characters";
    if (!/[A-Z]/.test(value)) return t("password_no_uppercase") || "Must contain at least one uppercase letter";
    if (!/[0-9]/.test(value)) return t("password_no_number")    || "Must contain at least one number";
    return "";
  };

  const validateConfirmPassword = (value) => {
    if (!value)                return t("confirm_password_required") || "Please confirm your password";
    if (value !== formData.p1) return t("passwords_do_not_match")    || "Passwords do not match";
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) setFieldErrors(prev => ({ ...prev, [name]: "" }));
    if (status === "error") { setStatus("idle"); setErrorMsg(""); }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = "";
    if (name === "username") error = validateUsername(value);
    else if (name === "email") error = validateEmail(value);
    else if (name === "p1")    error = validatePassword(value);
    else if (name === "p2")    error = validateConfirmPassword(value);
    setFieldErrors(prev => ({ ...prev, [name]: error }));
  };

  const validateForm = () => {
    const errors = {
      username: validateUsername(formData.username),
      email:    validateEmail(formData.email),
      p1:       validatePassword(formData.p1),
      p2:       validateConfirmPassword(formData.p2)
    };
    setFieldErrors(errors);
    return !Object.values(errors).some(err => err);
  };

  // ── OTP timer ─────────────────────────────────────────────────────────────
  const startTimer = () => {
    clearInterval(timerRef.current);
    setCountdown(OTP_EXPIRY);
    timerRef.current = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(timerRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  // ── Step 1: Register ──────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validateForm()) { setStatus("idle"); return; }

    setStatus("loading");
    setErrorMsg("");

    try {
      // 1. Djoser Registration
      await axios.post(`${API_BASE}/auth/users/`, {
        username:    formData.username,
        email:       formData.email,
        password:    formData.p1,
        re_password: formData.p2,
      });

      // 2. Send OTP to verify email (also marks user inactive until verified)
      await axios.post(`${API_BASE}/auth/email/send-otp/`, {
        email: formData.email,
      });

      // 3. Go to OTP step
      setStatus("idle");
      setStep("otp");
      startTimer();
      setTimeout(() => boxRefs.current[0]?.focus(), 100);

    } catch (err) {
      setStatus("error");
      const data = err.response?.data;

      if (data?.email) {
        const msg = Array.isArray(data.email) ? data.email[0] : data.email;
        setFieldErrors(prev => ({ ...prev, email: t("email_already_registered") || "This email is already registered." }));
        setErrorMsg(t("email_already_registered") || "This email is already registered.");
        return;
      }
      if (data?.username) {
        const msg = Array.isArray(data.username) ? data.username[0] : data.username;
        setFieldErrors(prev => ({ ...prev, username: t("username_taken") || "This username is already taken." }));
        setErrorMsg(t("username_taken") || "This username is already taken.");
        return;
      }
      if (data?.password) {
        const msg = Array.isArray(data.password) ? data.password[0] : data.password;
        setFieldErrors(prev => ({ ...prev, p1: msg }));
        setErrorMsg(msg);
        return;
      }
      if (Array.isArray(data)) {
        const msg = data[0] || "";
        if (msg.toLowerCase().includes("unable")) {
          setFieldErrors(prev => ({ ...prev, email: t("email_already_registered") || "Email already registered." }));
          setErrorMsg(t("email_already_registered") || "Email already registered.");
        } else {
          setErrorMsg(msg || t("reg_failed") || "Registration failed.");
        }
        return;
      }
      if (typeof data === "object" && data !== null) {
        const errorList = Object.entries(data)
          .map(([key, val]) => Array.isArray(val) ? val[0] : val)
          .filter(e => typeof e === "string");
        setErrorMsg(errorList[0] || t("reg_failed") || "Registration failed.");
      } else {
        setErrorMsg(t("reg_failed") || "Registration failed. Please try again.");
      }
    }
  };

  // ── OTP digit handlers ────────────────────────────────────────────────────
  const handleDigitChange = (i, val) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next  = [...digits]; next[i] = digit;
    setDigits(next);
    setOtpError("");
    if (digit && i < OTP_LENGTH - 1) boxRefs.current[i + 1]?.focus();
  };

  const handleDigitKey = (i, e) => {
    if (e.key === "Backspace") {
      if (!digits[i] && i > 0) {
        const next = [...digits]; next[i - 1] = "";
        setDigits(next); boxRefs.current[i - 1]?.focus();
      } else {
        const next = [...digits]; next[i] = ""; setDigits(next);
      }
    } else if (e.key === "ArrowLeft"  && i > 0)              boxRefs.current[i - 1]?.focus();
    else if   (e.key === "ArrowRight" && i < OTP_LENGTH - 1) boxRefs.current[i + 1]?.focus();
  };

  const handleDigitPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    const next   = [...digits];
    [...pasted].forEach((ch, idx) => { if (idx < OTP_LENGTH) next[idx] = ch; });
    setDigits(next);
    boxRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  // ── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < OTP_LENGTH) {
      setOtpError(t("otp_incomplete") || "Please enter the full 6-digit code.");
      return;
    }

    setOtpStatus("loading");
    setOtpError("");

    try {
      // 1. Verify OTP → activates account
      await axios.post(`${API_BASE}/auth/email/verify-otp/`, {
        email: formData.email,
        code,
      });

      // 2. Auto-login
      const tokenRes = await axios.post(`${API_BASE}/token/`, {
        username: formData.username,
        password: formData.p1,
      });

      const { access, refresh } = tokenRes.data;
      localStorage.setItem("accessToken", access);
      if (refresh) localStorage.setItem("refreshToken", refresh);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access}`;

      setOtpStatus("success");
      clearInterval(timerRef.current);
      setTimeout(() => navigate("/", { replace: true }), 1200);

    } catch (err) {
      setOtpStatus("error");
      const data = err.response?.data;
      const msg  = data?.error || data?.detail || (Array.isArray(data) ? data[0] : null);
      setOtpError(t("otp_invalid") || msg || "Invalid or expired code.");
      setDigits(Array(OTP_LENGTH).fill(""));
      setTimeout(() => boxRefs.current[0]?.focus(), 60);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    setResendSt("loading");
    try {
      await axios.post(`${API_BASE}/auth/email/send-otp/`, { email: formData.email });
      setResendSt("sent");
      setDigits(Array(OTP_LENGTH).fill(""));
      startTimer();
      setTimeout(() => { setResendSt("idle"); boxRefs.current[0]?.focus(); }, 2500);
    } catch {
      setResendSt("idle");
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`register-page ${isRTL ? "rtl" : ""}`}>
      <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>
      <style>{`
        :root {
          --primary: #0c3cff;
          --primary-dark: #0a2dd6;
          --primary-light: #3d5dff;
          --success: #10b981;
          --error: #ef4444;
          --bg-primary: #fbfcff;
          --bg-secondary: #f8fafc;
          --surface: #ffffff;
          --text-primary: #061226;
          --text-secondary: #6b7280;
          --border: #e5e7eb;
          --border-light: #f3f4f6;
          --radius-sm: 8px;
          --radius-md: 12px;
          --radius-lg: 16px;
          --shadow-sm: 0 2px 8px rgba(10, 18, 40, 0.04);
          --shadow-md: 0 8px 24px rgba(10, 18, 40, 0.08);
          --shadow-lg: 0 20px 60px rgba(10, 18, 40, 0.12);
          --transition: cubic-bezier(0.2, 0.8, 0.2, 1);
        }

        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; }

        .register-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #fbfcff 0%, #f0f4ff 50%, #f8fafc 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          color: var(--text-primary);
          overflow-x: hidden;
        }

        .register-page.rtl { direction: rtl; }

        .register-page::before,
        .register-page::after {
          content: "";
          position: fixed;
          border-radius: 50%;
          opacity: 0.03;
          pointer-events: none;
          z-index: 0;
        }
        .register-page::before {
          width: 500px; height: 500px;
          background: var(--primary);
          top: -200px; right: -200px;
          animation: float 25s ease-in-out infinite;
        }
        .register-page::after {
          width: 400px; height: 400px;
          background: var(--success);
          bottom: -150px; left: -150px;
          animation: float 30s ease-in-out infinite reverse;
        }

        .register-wrapper {
          width: 100%;
          max-width: 520px;
          position: relative;
          z-index: 1;
          animation: slideUp 0.8s var(--transition);
        }

        .register-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }
        .register-card::before {
          content: "";
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--primary), var(--primary-light));
        }
        .register-card.error   { border-color: var(--error);   animation: shake 0.5s ease; }
        .register-card.success { border-color: var(--success); }

        .register-content { padding: 48px 40px; }

        .register-header {
          margin-bottom: 32px;
          animation: fadeInDown 0.6s var(--transition);
        }
        .register-brand { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .register-dot {
          width: 12px; height: 12px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }
        .register-header h1 { font-size: 32px; font-weight: 700; margin: 0 0 8px 0; line-height: 1.2; letter-spacing: -0.5px; }
        .register-header p  { font-size: 14px; color: var(--text-secondary); margin: 0; font-weight: 500; }

        /* ── Step indicator (only shown during OTP step) ── */
        .reg-steps {
          display: flex; align-items: flex-start;
          margin-bottom: 28px; padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }
        .reg-step { display: flex; flex-direction: column; align-items: center; gap: 6px; flex: 1; }
        .reg-step-bubble {
          width: 36px; height: 36px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700;
          border: 2px solid var(--border);
          background: var(--bg-secondary); color: var(--text-secondary);
          transition: all 0.3s var(--transition);
        }
        .reg-step.active .reg-step-bubble {
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          border-color: transparent; color: #fff;
          box-shadow: 0 4px 14px rgba(12,60,255,.3);
          animation: pulse-step 2s ease-in-out infinite;
        }
        .reg-step.done .reg-step-bubble {
          background: linear-gradient(135deg, var(--success), #059669);
          border-color: transparent; color: #fff;
          box-shadow: 0 4px 12px rgba(16,185,129,.25);
        }
        .reg-step.upcoming .reg-step-bubble { opacity: .5; }
        .reg-step span { font-size: 11px; font-weight: 600; color: var(--text-secondary); letter-spacing: .03em; }
        .reg-step.active span { color: var(--primary); font-weight: 700; }
        .reg-step.done  span  { color: var(--success); }
        .reg-step-line { flex: 1; height: 2px; background: var(--border); margin-top: 17px; transition: background .4s; }
        .reg-step-line.done { background: linear-gradient(90deg, var(--success), rgba(16,185,129,.3)); }

        /* ── Form ── */
        .register-form { display: flex; flex-direction: column; gap: 20px; }
        .form-group {
          display: flex; flex-direction: column; gap: 8px;
          animation: fadeInUp 0.6s var(--transition);
        }
        .form-group:nth-child(1) { animation-delay: 0.1s; }
        .form-group:nth-child(2) { animation-delay: 0.15s; }
        .form-group:nth-child(3) { animation-delay: 0.2s; }
        .form-group:nth-child(4) { animation-delay: 0.25s; }

        .form-label { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-primary); opacity: 0.8; }
        .form-input-wrapper { position: relative; display: flex; align-items: center; }

        .form-input {
          width: 100%; padding: 12px 16px;
          background: var(--bg-secondary);
          border: 1.5px solid var(--border); border-radius: var(--radius-md);
          font-size: 15px; line-height: 1.5; font-family: inherit; color: var(--text-primary);
          transition: all 0.2s ease;
        }
        .form-input::placeholder { color: #b3b9c8; }
        .form-input:focus { outline: none; background: var(--surface); border-color: var(--primary); box-shadow: 0 0 0 3px rgba(12,60,255,0.1); transform: translateY(-1px); }
        .form-input.error { border-color: var(--error); background: rgba(239,68,68,0.02); }
        .form-input.error:focus { box-shadow: 0 0 0 3px rgba(239,68,68,0.1); }

        .password-toggle {
          position: absolute; right: 12px;
          background: none; border: none; color: var(--text-secondary);
          font-size: 12px; font-weight: 700; cursor: pointer;
          padding: 6px 10px; border-radius: 6px; transition: all 0.2s ease;
          text-transform: uppercase; letter-spacing: 0.04em;
        }
        .register-page.rtl .password-toggle { right: auto; left: 12px; }
        .password-toggle:hover { background: var(--border-light); color: var(--text-primary); }

        .field-error { font-size: 12px; color: var(--error); margin-top: 4px; display: flex; align-items: center; gap: 6px; animation: slideDown 0.2s ease; }

        .strength-meter { display: flex; align-items: center; gap: 10px; margin-top: 8px; padding: 10px; background: var(--border-light); border-radius: var(--radius-sm); }
        .strength-bars  { display: flex; gap: 4px; flex: 1; }
        .strength-bar   { height: 4px; flex: 1; background: #e5e7eb; border-radius: 2px; transition: all 0.3s ease; }
        .strength-bar.active { background: currentColor; }
        .strength-text  { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; min-width: 50px; text-align: right; }

        .form-error {
          padding: 12px 14px; background: rgba(239,68,68,0.06);
          border: 1px solid rgba(239,68,68,0.2); border-radius: var(--radius-sm);
          font-size: 13px; color: #b91c1c; font-weight: 500;
          display: flex; align-items: center; gap: 10px; animation: shake 0.3s ease;
        }
        .form-error::before { content: "⚠"; font-size: 16px; flex-shrink: 0; }

        .form-success {
          padding: 12px 14px; background: rgba(16,185,129,0.06);
          border: 1px solid rgba(16,185,129,0.2); border-radius: var(--radius-sm);
          font-size: 13px; color: #065f46; font-weight: 500;
          display: flex; align-items: center; gap: 10px; animation: slideDown 0.3s ease;
        }
        .form-success::before { content: "✓"; font-size: 16px; flex-shrink: 0; }

        .submit-button {
          position: relative; overflow: hidden;
          width: 100%; padding: 13px 24px; margin-top: 12px;
          background: linear-gradient(135deg, var(--primary), var(--primary-light));
          color: white; border: none; border-radius: var(--radius-md);
          font-size: 15px; font-weight: 700; letter-spacing: 0.03em;
          cursor: pointer; transition: all 0.2s ease;
          box-shadow: 0 4px 12px rgba(12,60,255,0.2);
          display: flex; align-items: center; justify-content: center; gap: 10px; min-height: 44px;
        }
        .submit-button:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(12,60,255,0.3); }
        .submit-button:active:not(:disabled) { transform: translateY(0); }
        .submit-button:disabled { opacity: 0.7; cursor: not-allowed; }
        .submit-button.loading { background: linear-gradient(135deg, #7c5cff, #6b5aff); }
        .submit-button.success { background: linear-gradient(135deg, var(--success), #059669); }

        .button-content { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; }
        .button-spinner { width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.4); border-top-color: white; border-radius: 50%; animation: spin 0.8s linear infinite; }
        .button-check   { animation: scaleIn 0.4s var(--transition); }

        /* ── OTP boxes ── */
        .otp-row { display: flex; gap: 10px; justify-content: center; }
        .otp-box {
          width: 52px; height: 60px; text-align: center;
          font-size: 24px; font-weight: 700;
          border: 2px solid var(--border); border-radius: var(--radius-md);
          background: var(--bg-secondary); color: var(--text-primary);
          caret-color: var(--primary); transition: all 0.18s var(--transition);
          outline: none; font-family: "SF Mono","Fira Code",monospace,inherit;
        }
        .otp-box:focus  { border-color: var(--primary); box-shadow: 0 0 0 3px rgba(12,60,255,.12); background: var(--surface); transform: translateY(-2px); }
        .otp-box.filled { border-color: var(--primary); background: rgba(12,60,255,.03); }
        .otp-box.error  { border-color: var(--error); background: rgba(239,68,68,.04); animation: shake .4s ease; }

        .otp-timer { display: flex; align-items: center; justify-content: center; gap: 7px; font-size: 13px; color: var(--text-secondary); }
        .otp-countdown { font-variant-numeric: tabular-nums; color: var(--primary); font-weight: 700; }
        .otp-expired   { color: var(--error); font-weight: 600; font-size: 13px; }

        .resend-row { display: flex; align-items: center; justify-content: center; gap: 8px; font-size: 13px; }
        .resend-text { color: var(--text-secondary); }
        .resend-btn  {
          background: none; border: none; cursor: pointer;
          color: var(--primary); font-weight: 700; font-size: 13px; font-family: inherit;
          padding: 4px 6px; border-radius: 5px; transition: all .18s;
        }
        .resend-btn:hover:not(:disabled) { background: rgba(12,60,255,.07); }
        .resend-btn:disabled             { opacity: .5; cursor: not-allowed; color: var(--text-secondary); }

        .back-btn {
          background: none; border: none; cursor: pointer;
          color: var(--text-secondary); font-size: 14px; font-weight: 600;
          font-family: inherit; padding: 8px; border-radius: var(--radius-sm);
          transition: color .18s; display: flex; align-items: center; justify-content: center;
        }
        .back-btn:hover { color: var(--primary); }

        /* ── Footer ── */
        .register-footer {
          margin-top: 28px; text-align: center; padding-top: 24px;
          border-top: 1px solid var(--border-light); font-size: 14px; color: var(--text-secondary);
          animation: fadeInUp 0.6s var(--transition); animation-delay: 0.3s;
        }
        .register-footer-text { display: inline; }
        .login-link { color: var(--primary); text-decoration: none; font-weight: 700; margin-left: 4px; transition: all 0.2s ease; }
        .login-link:hover { text-decoration: underline; color: var(--primary-dark); }

        .legal-notice {
          margin-top: 48px; font-size: 12px; color: #a0a8b8;
          text-align: center; font-weight: 500; opacity: 0.7;
          animation: fadeIn 0.6s var(--transition); animation-delay: 0.4s;
        }

        /* ── Animations ── */
        @keyframes slideUp    { from { opacity: 0; transform: translateY(30px); }  to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInDown { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeInUp   { from { opacity: 0; transform: translateY(10px); }  to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn     { from { opacity: 0; } to { opacity: 1; } }
        @keyframes shake      { 0%, 100% { transform: translateX(0); }  25% { transform: translateX(-8px); }  75% { transform: translateX(8px); } }
        @keyframes spin       { to { transform: rotate(360deg); } }
        @keyframes pulse      { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }
        @keyframes pulse-step { 0%,100% { box-shadow: 0 4px 14px rgba(12,60,255,.3); } 50% { box-shadow: 0 4px 22px rgba(12,60,255,.55); } }
        @keyframes float      { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(30px); } }
        @keyframes slideDown  { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn    { from { opacity: 0; transform: scale(0.8); }        to { opacity: 1; transform: scale(1); } }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { animation: none !important; transition: none !important; }
        }

        @media (max-width: 520px) {
          .register-page    { padding: 16px; }
          .register-content { padding: 32px 24px; }
          .register-header h1 { font-size: 26px; }
          .register-header  { margin-bottom: 28px; }
          .register-form    { gap: 18px; }
          .submit-button    { padding: 12px 20px; }
          .register-card    { border-radius: var(--radius-md); box-shadow: 0 10px 30px rgba(10,18,40,0.1); }
          .register-page::before { width: 300px; height: 300px; top: -150px; right: -150px; }
          .register-page::after  { width: 250px; height: 250px; bottom: -100px; left: -100px; }
          .otp-box { width: 44px; height: 52px; font-size: 20px; }
          .otp-row { gap: 8px; }
        }
        @media (max-width: 380px) {
          .register-content { padding: 24px 18px; }
          .register-header h1 { font-size: 24px; }
          .form-label { font-size: 11px; }
          .form-input { font-size: 14px; padding: 10px 14px; }
          .otp-row { gap: 6px; }
          .otp-box { width: 38px; height: 46px; font-size: 18px; }
        }
      `}</style>

      <div className="register-wrapper">
        <div
          className={`register-card ${
            (status === "error" || otpStatus === "error") ? "error" :
            (status === "success" || otpStatus === "success") ? "success" : ""
          }`}
          role="main"
          aria-labelledby="register-heading"
        >
          <div className="register-content">

            {/* ════════════════════ STEP 1: REGISTRATION FORM ══════════════ */}
            {step === "form" && (
              <>
                <header className="register-header">
                  <div className="register-brand">
                    <div className="register-dot" />
                    <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>24News</span>
                  </div>
                  <h1 id="register-heading">{t("create_account")}</h1>
                  <p>{t("get_started")}</p>
                </header>

                <form onSubmit={handleRegister} className="register-form" noValidate>

                  {/* Username */}
                  <div className="form-group">
                    <label htmlFor="username" className="form-label">{t("username_aria")}</label>
                    <div className="form-input-wrapper">
                      <input
                        id="username" name="username" type="text" required
                        placeholder={t("username_placeholder") || "john_doe"}
                        value={formData.username}
                        onChange={handleChange} onBlur={handleBlur}
                        disabled={status === "loading" || status === "success"}
                        className={`form-input ${fieldErrors.username ? "error" : ""}`}
                        aria-label={t("username")}
                        aria-invalid={!!fieldErrors.username}
                        aria-describedby={fieldErrors.username ? "username-error" : undefined}
                      />
                    </div>
                    {fieldErrors.username && (
                      <div className="field-error" id="username-error" role="alert">
                        <span>⚠</span> {fieldErrors.username}
                      </div>
                    )}
                  </div>

                  {/* Email */}
                  <div className="form-group">
                    <label htmlFor="email" className="form-label">{t("email_aria")}</label>
                    <div className="form-input-wrapper">
                      <input
                        id="email" name="email" type="email" required
                        placeholder={t("email_placeholder") || "you@example.com"}
                        value={formData.email}
                        onChange={handleChange} onBlur={handleBlur}
                        disabled={status === "loading" || status === "success"}
                        className={`form-input ${fieldErrors.email ? "error" : ""}`}
                        aria-label={t("email")}
                        aria-invalid={!!fieldErrors.email}
                        aria-describedby={fieldErrors.email ? "email-error" : undefined}
                      />
                    </div>
                    {fieldErrors.email && (
                      <div className="field-error" id="email-error" role="alert">
                        <span>⚠</span> {fieldErrors.email}
                      </div>
                    )}
                  </div>

                  {/* Password */}
                  <div className="form-group">
                    <label htmlFor="password" className="form-label">{t("password")}</label>
                    <div className="form-input-wrapper">
                      <input
                        id="password" name="p1"
                        type={showPasswords.p1 ? "text" : "password"}
                        required placeholder="••••••••"
                        value={formData.p1}
                        onChange={handleChange} onBlur={handleBlur}
                        disabled={status === "loading" || status === "success"}
                        className={`form-input ${fieldErrors.p1 ? "error" : ""}`}
                        aria-label={t("password")}
                        aria-invalid={!!fieldErrors.p1}
                        aria-describedby={fieldErrors.p1 ? "password-error" : "password-strength"}
                      />
                      <button type="button" className="password-toggle"
                        onClick={() => setShowPasswords(p => ({ ...p, p1: !p.p1 }))}
                        aria-pressed={showPasswords.p1}
                        aria-label={showPasswords.p1 ? t("hide_password") || "Hide password" : t("show_password") || "Show password"}>
                        {showPasswords.p1 ? t("Hide") : t("Show")}
                      </button>
                    </div>
                    {fieldErrors.p1 && (
                      <div className="field-error" id="password-error" role="alert">
                        <span>⚠</span> {fieldErrors.p1}
                      </div>
                    )}
                    {formData.p1 && !fieldErrors.p1 && (
                      <div className="strength-meter" id="password-strength">
                        <div className="strength-bars">
                          {[1,2,3,4].map(bar => (
                            <div key={bar}
                              className={`strength-bar ${strength >= bar ? "active" : ""}`}
                              style={{ color: strength >= bar ? getStrengthColor(strength) : undefined }}
                            />
                          ))}
                        </div>
                        <span className="strength-text" style={{ color: getStrengthColor(strength) }}>
                          {getStrengthLabel()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div className="form-group">
                    <label htmlFor="confirm-password" className="form-label">{t("confirm_password")}</label>
                    <div className="form-input-wrapper">
                      <input
                        id="confirm-password" name="p2"
                        type={showPasswords.p2 ? "text" : "password"}
                        required placeholder="••••••••"
                        value={formData.p2}
                        onChange={handleChange} onBlur={handleBlur}
                        disabled={status === "loading" || status === "success"}
                        className={`form-input ${fieldErrors.p2 ? "error" : ""}`}
                        aria-label={t("confirm_password")}
                        aria-invalid={!!fieldErrors.p2}
                        aria-describedby={fieldErrors.p2 ? "confirm-error" : undefined}
                      />
                      <button type="button" className="password-toggle"
                        onClick={() => setShowPasswords(p => ({ ...p, p2: !p.p2 }))}
                        aria-pressed={showPasswords.p2}
                        aria-label={showPasswords.p2 ? t("hide_password") || "Hide password" : t("show_password") || "Show password"}>
                        {showPasswords.p2 ? t("Hide") : t("Show")}
                      </button>
                    </div>
                    {fieldErrors.p2 && (
                      <div className="field-error" id="confirm-error" role="alert">
                        <span>⚠</span> {fieldErrors.p2}
                      </div>
                    )}
                  </div>

                  {status === "error" && (
                    <div className="form-error" role="alert" aria-live="assertive">
                      {errorMsg}
                    </div>
                  )}

                  {status === "success" && (
                    <div className="form-success" role="status" aria-live="polite">
                      {t("reg_success") || "Registration successful! Redirecting..."}
                    </div>
                  )}

                  <button type="submit"
                    className={`submit-button ${status}`}
                    disabled={status === "loading" || status === "success"}
                    aria-busy={status === "loading"}>
                    <span className="button-content">
                      {status === "loading" && <div className="button-spinner" aria-hidden="true" />}
                      {status === "success" && <span className="button-check" aria-hidden="true">✓</span>}
                      {status === "idle"    && t("register")}
                      {status === "loading" && (t("creating_account") || "Creating account...")}
                      {status === "success" && (t("success") || "Success!")}
                    </span>
                  </button>
                </form>

                <footer className="register-footer">
                  <span className="register-footer-text">{t("already_account")}</span>
                  <Link to="/login" className="login-link">{t("login")}</Link>
                </footer>
              </>
            )}

            {/* ════════════════════ STEP 2: OTP VERIFICATION ═══════════════ */}
            {step === "otp" && (
              <>
                <header className="register-header">
                  <div className="register-brand">
                    <div className="register-dot" />
                    <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--text-primary)" }}>24News</span>
                  </div>

                  {/* Step indicator */}
                  <div className="reg-steps">
                    <div className="reg-step done">
                      <div className="reg-step-bubble">✓</div>
                      <span>{t("step_account") || "Account"}</span>
                    </div>
                    <div className="reg-step-line done" />
                    <div className="reg-step active">
                      <div className="reg-step-bubble">2</div>
                      <span>{t("step_verify_code") || "Verify"}</span>
                    </div>
                  </div>

                  <h1 id="register-heading">{t("verify_your_identity") || "Verify your email"}</h1>
                  <p>
                    {t("otp_sent_to") || "Code sent to"}{" "}
                    <strong style={{ color: "var(--text-primary)" }}>{formData.email}</strong>
                  </p>
                </header>

                <form onSubmit={handleVerifyOtp} className="register-form" noValidate>

                  {/* OTP digit boxes */}
                  <div className="form-group">
                    <label className="form-label">{t("otp_label") || "Verification code"}</label>
                    <div className="otp-row">
                      {digits.map((d, i) => (
                        <input
                          key={i}
                          ref={el => boxRefs.current[i] = el}
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={1}
                          value={d}
                          onChange={e => handleDigitChange(i, e.target.value)}
                          onKeyDown={e => handleDigitKey(i, e)}
                          onPaste={handleDigitPaste}
                          disabled={otpStatus === "loading" || otpStatus === "success"}
                          className={`otp-box ${otpError ? "error" : ""} ${d ? "filled" : ""}`}
                          aria-label={`${t("digit") || "Digit"} ${i + 1}`}
                          autoComplete={i === 0 ? "one-time-code" : "off"}
                        />
                      ))}
                    </div>
                    {otpError && (
                      <div className="field-error" role="alert">
                        <span>⚠</span> {otpError}
                      </div>
                    )}
                  </div>

                  {/* Countdown timer */}
                  <div className="otp-timer">
                    {countdown > 0
                      ? <><span>⏱</span>{" "}{t("otp_expires_in") || "Expires in"}{" "}<strong className="otp-countdown">{fmtTime(countdown)}</strong></>
                      : <span className="otp-expired">{t("otp_expired") || "Code has expired."}</span>
                    }
                  </div>

                  {otpStatus === "success" && (
                    <div className="form-success" role="status" aria-live="polite">
                      {t("email_verified") || "Email verified! Signing you in..."}
                    </div>
                  )}

                  <button type="submit"
                    className={`submit-button ${otpStatus}`}
                    disabled={otpStatus === "loading" || otpStatus === "success" || digits.join("").length < OTP_LENGTH}
                    aria-busy={otpStatus === "loading"}>
                    <span className="button-content">
                      {otpStatus === "loading" && <div className="button-spinner" aria-hidden="true" />}
                      {otpStatus === "success" && <span className="button-check" aria-hidden="true">✓</span>}
                      {otpStatus === "loading"  ? (t("verifying")   || "Verifying...")
                      : otpStatus === "success" ? (t("success")     || "Success!")
                      :                           (t("verify_code") || "Verify")}
                    </span>
                  </button>

                  {/* Resend */}
                  <div className="resend-row">
                    <span className="resend-text">{t("didnt_receive") || "Didn't receive it?"}</span>
                    <button type="button" className="resend-btn"
                      onClick={handleResend}
                      disabled={resendSt === "loading" || countdown > 0}>
                      {resendSt === "sent"    ? (t("resent")      || "✓ Sent!")
                      : resendSt === "loading" ? (t("resending")   || "Resending...")
                      : countdown > 0          ? `${t("resend_in") || "Resend in"} ${fmtTime(countdown)}`
                      :                          (t("resend_code") || "Resend code")}
                    </button>
                  </div>

                  {/* Back to form */}
                  <button type="button" className="back-btn"
                    onClick={() => {
                      setStep("form");
                      setDigits(Array(OTP_LENGTH).fill(""));
                      setOtpError("");
                      setOtpStatus("idle");
                      clearInterval(timerRef.current);
                    }}>
                    ← {t("back") || "Back"}
                  </button>
                </form>
              </>
            )}

          </div>
        </div>
      </div>

      <div className="legal-notice">
        © {new Date().getFullYear()} {t("news_platform") || "Newsroom"}. {t("all_rights_reserved")}
      </div>
    </div>
  );
}